import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import { useGetBuyerQuery } from "@/src/store/apis/client/buyer";

// Types
export type ButtonActionType =
  | "LINK_PARTNER"
  | "REQUEST_SIGNATURE"
  | "VIEW_CONTRACT"
  | "VIEW_REJECTION_REASON"
  | "CREATE_PO"
  | "CREATE_SC"
  | "NONE";

export interface ProgressStep {
  stageNumber: 1 | 2 | 3;
  documentType: "SC" | "PO" | null;
  status: "completed" | "active" | "pending" | "error" | "final";
  title: string;
  message?: string;
  buttonText?: string;
  showButton: boolean;
  buttonAction?: ButtonActionType;
}

export interface ContractSignature {
  isExporterSignedPO: boolean;
  isImporterSignedPO: boolean;
  isExporterSignedSC: boolean;
  isImporterSignedSC: boolean;
}

export interface SignatureHistory {
  createdByRole?: "EXPORTER" | "IMPORTER";
  exporterScSignMedia?: any;
  importerScSignMedia?: any;
  exporterPOSignMedia?: any;
  importerPOSignMedia?: any;
}

export enum ContractStage {
  FIRST_DOCUMENT = 1,
  SECOND_DOCUMENT = 2,
  COMPLETED = 3,
}

export interface ContractStatusInfo {
  currentStage: ContractStage;
  currentStatus: string;
  exporterActionType: string;
  importerActionType: string;
  currentRound: number;
  flowType: "SC_FIRST" | "PO_FIRST";
}

export interface ContractData {
  signatureStatus: string;
  contractStatus: "PROCESSING" | "COMPLETE" | "NOT_LINKED";
  isRejected?: boolean;
  buyerId?: number;
  importerBuyerId?: number;
  poNo?: string;
  scNo?: string;
  contractStatusInfo?: ContractStatusInfo;
}

interface UseContractProgressProps {
  userRole: "EXPORTER" | "IMPORTER";
  contractData?: ContractData | null;
  contractSignature?: ContractSignature | null;
  signatureHistories?: SignatureHistory[] | null;
  contractId?: number;
  onAction?: (actionType: ButtonActionType) => void;
  rejectReason?: string;
}

type FlowType = "EXPORTER_LEAD" | "IMPORTER_LEAD";

interface ContractProgressState {
  flow: FlowType;
  stage: 1 | 2 | 3;
  status: string;
  userRole: "EXPORTER" | "IMPORTER";
  documentType: "SC" | "PO" | null;
}

export const useContractProgress = ({
  userRole,
  contractData,
  contractSignature,
  signatureHistories,
  contractId,
  onAction,
  rejectReason,
}: UseContractProgressProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation("contract");
  const [isExpanded, setIsExpanded] = useState(false);

  // 거래처 연동 상태 확인
  // 수출자는 buyerId, 수입자는 importerBuyerId를 사용
  const clientId =
    userRole === "EXPORTER"
      ? contractData?.buyerId
      : (contractData as any)?.importerBuyerId;

  const { data: buyerData } = useGetBuyerQuery(
    {
      id: clientId || 0,
    },
    {
      skip: !clientId,
    },
  );

  // 거래처 연동 여부 확인 - 서버 응답 우선 사용
  const isClientLinked = useMemo(() => {
    // 서버에서 제공하는 isClientLinked 우선 사용
    if (contractData?.contractStatusInfo?.isClientLinked !== undefined) {
      return contractData.contractStatusInfo.isClientLinked;
    }

    // 서버 정보가 없을 때만 기존 로직 사용 (하위 호환성)
    if (buyerData?.buyerQueryResultDto?.linkedStatus === "COMPLETED") {
      return true;
    }

    // 그 외의 경우 연동되지 않은 것으로 처리
    return false;
  }, [contractData?.contractStatusInfo, buyerData]);

  // 1. 플로우 확인 - 서버 데이터 우선 사용
  const flow = useMemo((): FlowType | null => {
    // 서버에서 제공하는 contractStatusInfo 우선 사용
    if (contractData?.contractStatusInfo?.flowType) {
      return contractData.contractStatusInfo.flowType === "SC_FIRST"
        ? "EXPORTER_LEAD"
        : "IMPORTER_LEAD";
    }

    // 서버 정보가 없을 때만 기존 로직 사용 (하위 호환성)
    if (!signatureHistories?.length) {
      return null;
    }

    // 가장 오래된 이력에서 플로우 결정
    const oldestHistory = signatureHistories[signatureHistories.length - 1];
    if (oldestHistory.createdByRole) {
      return oldestHistory.createdByRole === "EXPORTER"
        ? "EXPORTER_LEAD"
        : "IMPORTER_LEAD";
    }

    return userRole === "EXPORTER" ? "EXPORTER_LEAD" : "IMPORTER_LEAD";
  }, [contractData?.contractStatusInfo, signatureHistories, userRole]);

  // 2. 단계 확인 - 서버 데이터 우선 사용하되 서명 상태로 검증
  const stage = useMemo((): 1 | 2 | 3 => {
    // 서명 상태 확인
    const isPOFullySigned =
      contractSignature?.isExporterSignedPO &&
      contractSignature?.isImporterSignedPO;
    const isSCFullySigned =
      contractSignature?.isExporterSignedSC &&
      contractSignature?.isImporterSignedSC;

    // 모든 서명이 완료되었으면 무조건 3단계
    if (isPOFullySigned && isSCFullySigned) {
      return 3;
    }

    // 서버에서 제공하는 currentStage 사용 (서명 상태와 충돌하지 않는 경우)
    if (contractData?.contractStatusInfo?.currentStage) {
      const serverStage = contractData.contractStatusInfo.currentStage;

      // 서버 stage가 실제 서명 상태와 맞는지 검증
      if (!flow || !contractSignature) {
        return serverStage;
      }

      // 서버 stage가 서명 상태보다 높으면 서명 상태 기준으로 보정
      if (flow === "EXPORTER_LEAD") {
        const actualStage = isSCFullySigned ? 2 : 1;
        return Math.min(serverStage, actualStage);
      } else {
        const actualStage = isPOFullySigned ? 2 : 1;
        return Math.min(serverStage, actualStage);
      }
    }

    // 서버 정보가 없을 때 기존 로직 사용
    if (!flow || !contractSignature) return 1;

    if (flow === "EXPORTER_LEAD") {
      return isSCFullySigned ? 2 : 1;
    } else {
      return isPOFullySigned ? 2 : 1;
    }
  }, [contractData?.contractStatusInfo, flow, contractSignature]);

  // 3. 상태 확인 - 서버 데이터 우선 사용
  const status = useMemo((): string => {
    // 서버에서 제공하는 currentStatus 우선 사용
    if (contractData?.contractStatusInfo?.currentStatus) {
      return contractData.contractStatusInfo.currentStatus;
    }

    // 서버 정보가 없을 때만 기존 로직 사용 (하위 호환성)
    if (!contractData) {
      return isClientLinked ? "DRAFT_LINKED" : "DRAFT_NOT_LINKED";
    }

    if (stage === 3) {
      return "CONTRACT_LINKED";
    }

    return "DRAFT";
  }, [contractData?.contractStatusInfo, contractData, stage, isClientLinked]);

  // 현재 진행 상태
  const progressState = useMemo((): ContractProgressState | null => {
    const currentFlow =
      flow || (userRole === "EXPORTER" ? "EXPORTER_LEAD" : "IMPORTER_LEAD");
    const documentType =
      stage === 3 ? null : getDocumentForStage(currentFlow, stage);

    return {
      flow: currentFlow,
      stage,
      status,
      userRole,
      documentType,
    };
  }, [flow, stage, status, userRole]);

  // 현재 Round 정보
  const currentRound = useMemo(() => {
    return contractData?.contractStatusInfo?.currentRound || 1;
  }, [contractData?.contractStatusInfo]);

  // 3단계 UI 생성
  const stages = useMemo(():
    | [ProgressStep, ProgressStep, ProgressStep]
    | null => {
    if (!progressState) {
      return null;
    }

    const stages: [ProgressStep, ProgressStep, ProgressStep] = [
      {
        stageNumber: 1,
        documentType: null,
        status: "pending",
        title: "",
        message: "",
        buttonText: "",
        showButton: false,
        buttonAction: "NONE",
      },
      {
        stageNumber: 2,
        documentType: null,
        status: "pending",
        title: "",
        message: "",
        buttonText: "",
        showButton: false,
        buttonAction: "NONE",
      },
      {
        stageNumber: 3,
        documentType: null,
        status: "pending",
        title: "",
        message: "",
        buttonText: "",
        showButton: false,
        buttonAction: "NONE",
      },
    ];

    // 플로우에 따른 문서 타입 설정
    if (progressState.flow === "EXPORTER_LEAD") {
      stages[0].documentType = "SC";
      stages[1].documentType = "PO";
    } else {
      stages[0].documentType = "PO";
      stages[1].documentType = "SC";
    }

    // 현재 단계까지 completed 표시
    for (let i = 1; i < progressState.stage; i++) {
      stages[i - 1].status = "completed";
    }

    // 현재 단계 상태 설정
    if (progressState.stage <= 3) {
      const currentStageIndex = progressState.stage - 1;
      stages[currentStageIndex].status =
        progressState.status === "SC_REJECTED" ||
        progressState.status === "PO_REJECTED"
          ? "error"
          : progressState.stage === 3
            ? "final"
            : "active";
    }

    // 거래처 연동 상태 확인 - 위에서 계산한 isClientLinked 사용
    applyStageTexts(
      stages,
      progressState,
      t,
      isClientLinked,
      contractData?.contractStatusInfo,
    );

    return stages;
  }, [progressState, t, isClientLinked, contractData?.contractStatusInfo]);

  // 현재 단계 정보
  const currentStepInfo = useMemo(() => {
    if (!stages) return { step: null, index: -1 };

    const currentIndex = stages.findIndex(
      (s) =>
        s.status === "active" || s.status === "error" || s.status === "final",
    );
    return {
      step: stages[currentIndex] || null,
      index: currentIndex,
    };
  }, [stages]);

  // 액션 핸들러
  const handleButtonClick = (step: ProgressStep) => {
    if (!step.showButton || !step.buttonAction || step.buttonAction === "NONE")
      return;

    switch (step.buttonAction) {
      case "LINK_PARTNER":
        // 거래처 연동 페이지로 이동
        if (onAction) onAction("LINK_PARTNER");
        break;

      case "REQUEST_SIGNATURE":
        // 서명 요청
        if (onAction) onAction("REQUEST_SIGNATURE");
        break;

      case "CREATE_PO":
        navigate(`${IMPORTER_PRIVATE_PATH.CONTRACT_ADD}?id=${contractId}`);
        break;

      case "CREATE_SC":
        navigate(
          `${EXPORTER_PRIVATE_PATH.CONTRACT_ADD}?id=${contractId}&clientCheck=false`,
        );
        break;

      case "VIEW_CONTRACT":
        // 계약서 보기
        if (onAction) {
          onAction("VIEW_CONTRACT");
        }
        break;

      case "VIEW_REJECTION_REASON":
        // 반려 사유 보기
        if (onAction) onAction("VIEW_REJECTION_REASON");
        break;

      default:
        console.warn("Unknown button action:", step.buttonAction);
        break;
    }
  };

  // 색상 관련 유틸리티 함수들
  const getStepColor = (status: string) => {
    switch (status) {
      case "completed":
      case "final":
        return "#15BD66";
      case "active":
        return "#166DD7";
      case "error":
        return "#CC0000";
      case "pending":
      default:
        return "#808080";
    }
  };

  const getBackgroundColor = (status: string) => {
    switch (status) {
      case "completed":
        return "white";
      case "final":
        return "#F7F9FA"; // Gray 11
      case "active":
        return "#F0F8FF";
      case "error":
        return "#FFEFEF";
      case "pending":
      default:
        return "#F7F9FA";
    }
  };

  const getBorderColor = (status: string) => {
    switch (status) {
      case "completed":
        return "#E7EDF0"; // Gray 9
      case "final":
        return "#E7EDF0"; // gray9
      case "active":
        return "#D7E7F9";
      case "error":
        return "#FAD8D8";
      case "pending":
      default:
        return "#E7EDF0";
    }
  };

  return {
    // 상태
    stages: stages || [],
    currentStepInfo,
    isExpanded,
    currentRound,

    // 핸들러
    toggleExpanded: () => setIsExpanded(!isExpanded),
    handleButtonClick,

    // 유틸리티
    getStepColor,
    getBackgroundColor,
    getBorderColor,
  };
};

// Helper functions
function getDocumentForStage(
  flow: FlowType,
  stage: 1 | 2 | 3,
): "SC" | "PO" | null {
  if (stage === 3) return null;

  const documentOrder = {
    EXPORTER_LEAD: ["SC", "PO"] as const,
    IMPORTER_LEAD: ["PO", "SC"] as const,
  };

  return documentOrder[flow][stage - 1];
}

function getDocumentTypeFromContractStatus(
  contractStatusInfo: ContractStatusInfo,
): "SC" | "PO" | null {
  // currentStatus를 기준으로 현재 필요한 문서 타입 결정
  if (contractStatusInfo.currentStatus === "SC_SIGNATURE_REQUIRED") {
    return "SC";
  }
  if (contractStatusInfo.currentStatus === "PO_SIGNATURE_REQUIRED") {
    return "PO";
  }

  // currentStatus가 명확하지 않은 경우 flowType과 currentStage를 조합하여 판단
  const flow =
    contractStatusInfo.flowType === "SC_FIRST"
      ? "EXPORTER_LEAD"
      : "IMPORTER_LEAD";
  return getDocumentForStage(flow, contractStatusInfo.currentStage);
}

function applyStageTexts(
  stages: [ProgressStep, ProgressStep, ProgressStep],
  state: ContractProgressState,
  t: any,
  isClientLinked: boolean,
  contractStatusInfo?: ContractStatusInfo,
): void {
  stages.forEach((stage) => {
    // 현재 단계이거나 에러 상태인 경우
    if (stage.status === "active" || stage.status === "error") {
      // 서버에서 제공하는 액션 타입 사용
      if (contractStatusInfo && stage.stageNumber === state.stage) {
        const actionType =
          state.userRole === "EXPORTER"
            ? contractStatusInfo.exporterActionType
            : contractStatusInfo.importerActionType;

        // 액션 타입이 있을 때만 applyActionTypeTexts 사용
        if (actionType) {
          applyActionTypeTexts(stage, actionType, state, t, contractStatusInfo);
        } else {
          // 액션 타입이 없으면 기존 로직 사용
          applyCSVMappedTexts(stage, state, t, isClientLinked);
        }
      } else {
        // 서버 정보가 없을 때만 기존 로직 사용
        applyCSVMappedTexts(stage, state, t, isClientLinked);
      }
    }
    // completed 상태인 단계
    else if (stage.status === "completed") {
      if (stage.documentType === "PO") {
        stage.title = t("contract:status.notice.poSignedComplete.title");
      } else if (stage.documentType === "SC") {
        stage.title = t("contract:status.notice.scSignedComplete.title");
      }
    }

    // pending 상태인 단계
    else if (stage.status === "pending") {
      // 3단계는 계약 완료 대기
      if (stage.stageNumber === 3) {
        stage.title = t("contract:status.notice.contractComplete.title");
      } else if (stage.documentType) {
        // 2단계는 문서 타입에 따라 대기 중 표시
        if (stage.documentType === "PO") {
          stage.title = t("contract:status.notice.poSignedComplete.title");
        } else if (stage.documentType === "SC") {
          stage.title = t("contract:status.notice.scSignedComplete.title");
        }
      }
    }
    // final 상태 (3단계)
    else if (stage.status === "final") {
      stage.title = t("contract:status.notice.contractComplete.title");
      // 플로우에 따라 메시지 선택
      const messageKey =
        state.flow === "EXPORTER_LEAD"
          ? "contract:status.notice.contractComplete.messageExporterLead"
          : "contract:status.notice.contractComplete.messageImporterLead";
      stage.message = t(messageKey);
      stage.buttonText = t("contract:status.notice.contractComplete.button");
      stage.showButton = true;
      stage.buttonAction = "VIEW_CONTRACT";
    }
  });
}

function applyCSVMappedTexts(
  stage: ProgressStep,
  state: ContractProgressState,
  t: any,
  isClientLinked: boolean,
): void {
  const { flow, status, userRole } = state;
  const { stageNumber } = stage;

  const isInitiator =
    (flow === "EXPORTER_LEAD" && userRole === "EXPORTER") ||
    (flow === "IMPORTER_LEAD" && userRole === "IMPORTER");

  const mapping = getStateMapping(
    isInitiator,
    userRole,
    stageNumber,
    status,
    isClientLinked,
  );

  if (mapping) {
    stage.title = t(mapping.titleKey);
    if (mapping.messageKey) {
      stage.message = t(mapping.messageKey);
    }

    if (mapping.buttonAction !== "NONE") {
      stage.buttonText = mapping.buttonKey ? t(mapping.buttonKey) : "";
      stage.showButton = true;
      stage.buttonAction = mapping.buttonAction;
    } else if (mapping.statusKey) {
      stage.buttonText = t(mapping.statusKey);
      stage.showButton = false;
    }
  } else {
    if (stage.documentType === "PO") {
      stage.title = t("contract:status.notice.poDraft.title");
    } else if (stage.documentType === "SC") {
      stage.title = t("contract:status.notice.scDraft.title");
    } else {
      stage.title = t("contract:status.notice.contractProgress.title");
    }

    if (stageNumber === 1 && isDraftStatus(status)) {
      if (!isClientLinked) {
        stage.buttonText = t("contract:status.notice.scDraft.button");
        stage.showButton = true;
        stage.buttonAction = "LINK_PARTNER";
      } else {
        stage.buttonText = t(
          "contract:status.notice.scSignatureRequired.button",
        );
        stage.showButton = true;
        stage.buttonAction = "REQUEST_SIGNATURE";
      }
    }
  }
}

// Draft 상태 체크 헬퍼 함수
function isDraftStatus(status: string): boolean {
  // 의미론적 키를 기반으로 Draft 상태 확인
  return (
    status === "DRAFT" ||
    status === "DRAFT_LINKED" ||
    status === "DRAFT_NOT_LINKED" ||
    status === "SC_DRAFT_NOT_LINKED" ||
    status === "PO_DRAFT_NOT_LINKED"
  );
}

// 상태별 번역 키 매핑
function getStateMapping(
  isInitiator: boolean,
  userRole: "EXPORTER" | "IMPORTER",
  stageNumber: number,
  status: string,
  isClientLinked: boolean,
): {
  titleKey: string;
  messageKey?: string;
  buttonKey?: string;
  statusKey?: string;
  buttonAction: ButtonActionType;
} | null {
  // 수출자 Lead 플로우
  if (userRole === "EXPORTER" && isInitiator) {
    if (stageNumber === 1) {
      if (isDraftStatus(status) && !isClientLinked) {
        return {
          titleKey: "contract:status.notice.scDraft.title",
          messageKey: "contract:status.notice.scDraft.message",
          buttonKey: "contract:status.notice.scDraft.button",
          buttonAction: "LINK_PARTNER",
        };
      } else if (isDraftStatus(status) && isClientLinked) {
        return {
          titleKey: "contract:status.notice.scSignatureRequired.title",
          messageKey: "contract:status.notice.scSignatureRequired.message",
          buttonKey: "contract:status.notice.scSignatureRequired.button",
          buttonAction: "REQUEST_SIGNATURE",
        };
      } else if (status === "SENT") {
        return {
          titleKey: "contract:status.notice.scSignaturePending.title",
          messageKey: "contract:status.notice.scSignaturePending.message",
          statusKey: "contract:status.notice.scSignaturePending.button",
          buttonAction: "NONE",
        };
      } else if (status === "SC_PENDING") {
        return {
          titleKey: "contract:status.notice.scRejected.title",
          messageKey: "contract:status.notice.scRejected.message",
          buttonKey: "contract:status.notice.scRejected.button",
          buttonAction: "VIEW_REJECTION_REASON",
        };
      }
    } else if (stageNumber === 2) {
      if (status === "APPROVED") {
        // CSV 6번: 수출자 Lead 2단계 Approved - PO 서명 대기
        return {
          titleKey: "contract:status.notice.poSignaturePendingFromClient.title",
          messageKey:
            "contract:status.notice.poSignaturePendingFromClient.message",
          statusKey:
            "contract:status.notice.poSignaturePendingFromClient.button",
          buttonAction: "NONE",
        };
      } else if (status === "REQUESTED") {
        return {
          titleKey: "contract:status.notice.poSignatureRequired.title",
          messageKey: "contract:status.notice.poSignatureRequired.message",
          buttonKey: "contract:status.notice.poSignatureRequired.button",
          buttonAction: "VIEW_CONTRACT",
        };
      } else if (status === "PO_PENDING") {
        return {
          titleKey: "contract:status.notice.poRejected.title",
          messageKey: "contract:status.notice.poRejected.message",
          buttonKey: "contract:status.notice.poRejected.button",
          buttonAction: "VIEW_REJECTION_REASON",
        };
      }
    }
  }

  // 수입자 Lead 플로우
  else if (userRole === "IMPORTER" && isInitiator) {
    if (stageNumber === 1) {
      if (isDraftStatus(status) && !isClientLinked) {
        return {
          titleKey: "contract:status.notice.poDraft.title",
          messageKey: "contract:status.notice.poDraft.message",
          buttonKey: "contract:status.notice.poDraft.button",
          buttonAction: "LINK_PARTNER",
        };
      } else if (isDraftStatus(status) && isClientLinked) {
        return {
          titleKey: "contract:status.notice.poSignatureRequestNeeded.title",
          messageKey: "contract:status.notice.poSignatureRequestNeeded.message",
          buttonKey: "contract:status.notice.poSignatureRequestNeeded.button",
          buttonAction: "REQUEST_SIGNATURE",
        };
      } else if (
        status === "SENT" ||
        status === "PO_SIGNATURE_PENDING_FROM_CLIENT"
      ) {
        return {
          titleKey: "contract:status.notice.poSignatureRequestSent.title",
          messageKey: "contract:status.notice.poSignatureRequestSent.message",
          statusKey: "contract:status.notice.poSignatureRequestSent.button",
          buttonAction: "NONE",
        };
      } else if (status === "PO_PENDING") {
        // 수입자 Lead 1단계에서 수입자가 보는 PO 반려 - 수출자가 반려
        return {
          titleKey: "contract:status.notice.poRejected.title",
          messageKey: "contract:status.notice.poRejectedByClient.message",
          buttonKey: "contract:status.notice.poRejected.button",
          buttonAction: "VIEW_REJECTION_REASON",
        };
      }
    } else if (stageNumber === 2) {
      if (status === "APPROVED") {
        // CSV 14번: 수입자 Lead 2단계 Approved - SC 서명 대기
        return {
          titleKey: "contract:status.notice.scSignaturePendingFromClient.title",
          messageKey:
            "contract:status.notice.scSignaturePendingFromClient.message",
          statusKey:
            "contract:status.notice.scSignaturePendingFromClient.button",
          buttonAction: "NONE",
        };
      } else if (status === "REQUESTED" || status === "SC_SIGNATURE_REQUIRED") {
        return {
          titleKey: "contract:status.notice.scSignatureRequired.title",
          messageKey: "contract:status.notice.scSignatureRequired.message",
          buttonKey: "contract:status.notice.scSignatureRequired.button",
          buttonAction: "REQUEST_SIGNATURE",
        };
      } else if (status === "SC_PENDING") {
        // For importer lead flow, SC rejection means the importer rejected it
        // (importers can only reject SC, not create it)
        return {
          titleKey: "contract:status.notice.scRejected.title",
          messageKey: "contract:status.notice.scRejectedByMe.message",
          buttonKey: "contract:status.notice.scRejected.button",
          buttonAction: "VIEW_REJECTION_REASON",
        };
      }
    }
  }

  // 수출자 팔로우 플로우
  else if (userRole === "EXPORTER" && !isInitiator) {
    if (stageNumber === 1) {
      if (status === "SENT") {
        return {
          titleKey: "contract:status.notice.poSignatureRequired.title",
          messageKey: "contract:status.notice.poSignatureRequired.message",
          buttonKey: "contract:status.notice.poSignatureRequired.button",
          buttonAction: "VIEW_CONTRACT",
        };
      } else if (status === "PO_PENDING") {
        // 수출자가 수입자 리드 플로우에서 PO 반려를 보는 경우 - 수출자 본인이 반려
        return {
          titleKey: "contract:status.notice.poRejected.title",
          messageKey: "contract:status.notice.poRejected.message",
          buttonKey: "contract:status.notice.poRejected.button",
          buttonAction: "VIEW_REJECTION_REASON",
        };
      }
    } else if (stageNumber === 2) {
      if (status === "APPROVED") {
        return {
          titleKey: "contract:status.notice.scCreateRequired.title",
          messageKey: "contract:status.notice.scCreateRequired.message",
          buttonKey: "contract:status.notice.scCreateRequired.button",
          buttonAction: "CREATE_SC",
        };
      } else if (status === "REQUESTED") {
        return {
          titleKey: "contract:status.notice.scSignaturePending.title",
          messageKey: "contract:status.notice.scSignaturePending.message",
          statusKey: "contract:status.notice.scSignaturePending.button",
          buttonAction: "NONE",
        };
      } else if (status === "SC_PENDING") {
        return {
          titleKey: "contract:status.notice.scRejected.title",
          messageKey: "contract:status.notice.scRejected.message",
          buttonKey: "contract:status.notice.scRejected.button",
          buttonAction: "VIEW_REJECTION_REASON",
        };
      }
    }
  }

  // 수입자 팔로우 플로우
  else if (userRole === "IMPORTER" && !isInitiator) {
    if (stageNumber === 1) {
      if (status === "SENT" || status === "APPROVED") {
        return {
          titleKey: "contract:status.notice.scSignatureNeeded.title",
          messageKey: "contract:status.notice.scSignatureNeeded.message",
          buttonKey: "contract:status.notice.scSignatureNeeded.button",
          buttonAction: "VIEW_CONTRACT",
        };
      } else if (status === "SC_PENDING") {
        // For importer follow flow, SC rejection also means the importer rejected it
        // (importers can only reject SC, exporters create SC)
        return {
          titleKey: "contract:status.notice.scRejected.title",
          messageKey: "contract:status.notice.scRejectedByMe.message",
          buttonKey: "contract:status.notice.scRejected.button",
          buttonAction: "VIEW_REJECTION_REASON",
        };
      }
    } else if (stageNumber === 2) {
      if (status === "APPROVED") {
        return {
          titleKey: "contract:status.notice.poCreateRequired.title",
          messageKey: "contract:status.notice.poCreateRequired.message",
          buttonKey: "contract:status.notice.poCreateRequired.button",
          buttonAction: "CREATE_PO",
        };
      } else if (status === "REQUESTED") {
        return {
          titleKey: "contract:status.notice.poSignatureRequestSent.title",
          messageKey: "contract:status.notice.poSignatureRequestSent.message",
          statusKey: "contract:status.notice.poSignatureRequestSent.button",
          buttonAction: "NONE",
        };
      } else if (status === "PO_PENDING") {
        // 수입자 Follow 2단계에서 수입자가 보는 PO 반려 - 수출자가 반려
        return {
          titleKey: "contract:status.notice.poRejected.title",
          messageKey: "contract:status.notice.poRejectedByClient.message",
          buttonKey: "contract:status.notice.poRejected.button",
          buttonAction: "VIEW_REJECTION_REASON",
        };
      }
    }
  }

  return null;
}

// 서버 액션 타입에 따른 텍스트 적용
function applyActionTypeTexts(
  stage: ProgressStep,
  actionType: string,
  state: ContractProgressState,
  t: any,
  contractStatusInfo?: ContractStatusInfo,
): void {
  switch (actionType) {
    case "LINK_PARTNER":
      // Handle new status values for not linked drafts
      if (state.status === "SC_DRAFT_NOT_LINKED") {
        stage.title = t("contract:status.notice.scDraft.title");
        stage.message = t("contract:status.notice.scDraft.message");
        stage.buttonText = t("contract:status.notice.scDraft.button");
      } else if (state.status === "PO_DRAFT_NOT_LINKED") {
        stage.title = t("contract:status.notice.poDraft.title");
        stage.message = t("contract:status.notice.poDraft.message");
        stage.buttonText = t("contract:status.notice.poDraft.button");
      }
      stage.showButton = true;
      stage.buttonAction = "LINK_PARTNER";
      break;

    case "REQUEST_SIGNATURE":
      // 백엔드 데이터 기준으로 문서 타입 결정
      const documentType = contractStatusInfo
        ? getDocumentTypeFromContractStatus(contractStatusInfo)
        : stage.documentType;

      if (documentType === "SC") {
        // SC 서명 요청 필요
        stage.title = t("contract:status.notice.scSignatureRequired.title");
        stage.message = t("contract:status.notice.scSignatureRequired.message");
        stage.buttonText = t(
          "contract:status.notice.scSignatureRequired.button",
        );
        stage.buttonAction = "REQUEST_SIGNATURE";
      } else if (documentType === "PO") {
        // PO 서명 요청 필요
        stage.title = t(
          "contract:status.notice.poSignatureRequestNeeded.title",
        );
        stage.message = t(
          "contract:status.notice.poSignatureRequestNeeded.message",
        );
        stage.buttonText = t(
          "contract:status.notice.poSignatureRequestNeeded.button",
        );
        stage.buttonAction = "REQUEST_SIGNATURE";
      }
      stage.showButton = true;
      break;

    case "VIEW_CONTRACT":
      // 백엔드 데이터 기준으로 문서 타입 결정
      const viewDocumentType = contractStatusInfo
        ? getDocumentTypeFromContractStatus(contractStatusInfo)
        : stage.documentType;

      // 현재 상태에 따라 메시지 결정
      if (state.status.includes("SC")) {
        // SC 관련 상태
        if (state.status === "SC_SIGNATURE_REQUIRED") {
          // 서명 필요 (수신자 관점) - 계약서를 확인하고 서명해야 함
          stage.title = t("contract:status.notice.scSignatureNeeded.title");
          stage.message = t("contract:status.notice.scSignatureNeeded.message");
          stage.buttonText = t(
            "contract:status.notice.scSignatureNeeded.button",
          );
          stage.showButton = true;
          stage.buttonAction = "VIEW_CONTRACT";
        } else if (state.status === "SC_SIGNATURE_PENDING_FROM_CLIENT") {
          // 서명 대기 중 (발신자 관점)
          stage.title = t("contract:status.notice.scSignaturePending.title");
          stage.message = t(
            "contract:status.notice.scSignaturePending.message",
          );
          stage.buttonText = t(
            "contract:status.notice.scSignaturePending.button",
          );
          stage.showButton = false;
          stage.buttonAction = "NONE";
        } else {
          // 기타 SC 대기 상태
          stage.title = t("contract:status.notice.scSignaturePending.title");
          stage.message = t(
            "contract:status.notice.scSignaturePending.message",
          );
          stage.buttonText = t(
            "contract:status.notice.scSignaturePending.button",
          );
          stage.showButton = false;
          stage.buttonAction = "NONE";
        }
      } else if (state.status.includes("PO")) {
        // PO 관련 상태
        if (state.status === "PO_SIGNATURE_REQUIRED") {
          // 서명 필요 (수신자 관점) - 계약서를 확인하고 서명해야 함
          stage.title = t("contract:status.notice.poSignatureRequired.title");
          stage.message = t(
            "contract:status.notice.poSignatureRequired.message",
          );
          stage.buttonText = t(
            "contract:status.notice.poSignatureRequired.button",
          );
          stage.showButton = true;
          stage.buttonAction = "VIEW_CONTRACT";
        } else if (state.status === "PO_SIGNATURE_PENDING_FROM_CLIENT") {
          // 서명 대기 중 (발신자 관점)
          stage.title = t("contract:status.notice.poSignaturePending.title");
          stage.message = t(
            "contract:status.notice.poSignaturePending.message",
          );
          stage.buttonText = t(
            "contract:status.notice.poSignaturePending.button",
          );
          stage.showButton = false;
          stage.buttonAction = "NONE";
        } else {
          // 기타 PO 대기 상태
          stage.title = t("contract:status.notice.poSignaturePending.title");
          stage.message = t(
            "contract:status.notice.poSignaturePending.message",
          );
          stage.buttonText = t(
            "contract:status.notice.poSignaturePending.button",
          );
          stage.showButton = false;
          stage.buttonAction = "NONE";
        }
      } else {
        // 백엔드 데이터로 문서 타입 결정하여 폴백
        if (viewDocumentType === "SC") {
          stage.title = t("contract:status.notice.scSignaturePending.title");
          stage.message = t(
            "contract:status.notice.scSignaturePending.message",
          );
          stage.buttonText = t(
            "contract:status.notice.scSignaturePending.button",
          );
        } else {
          stage.title = t("contract:status.notice.poSignaturePending.title");
          stage.message = t(
            "contract:status.notice.poSignaturePending.message",
          );
          stage.buttonText = t(
            "contract:status.notice.poSignaturePending.button",
          );
        }
        stage.showButton = false;
        stage.buttonAction = "NONE";
      }
      break;

    case "CREATE_SC":
      stage.title = t("contract:status.notice.scCreateRequired.title");
      stage.message = t("contract:status.notice.scCreateRequired.message");
      stage.buttonText = t("contract:status.notice.scCreateRequired.button");
      stage.showButton = true;
      stage.buttonAction = "CREATE_SC";
      break;

    case "CREATE_PO":
      stage.title = t("contract:status.notice.poCreateRequired.title");
      stage.message = t("contract:status.notice.poCreateRequired.message");
      stage.buttonText = t("contract:status.notice.poCreateRequired.button");
      stage.showButton = true;
      stage.buttonAction = "CREATE_PO";
      break;

    case "VIEW_REJECTION_REASON":
      const rejectionDocumentType = contractStatusInfo
        ? getDocumentTypeFromContractStatus(contractStatusInfo)
        : stage.documentType;
      if (rejectionDocumentType === "SC") {
        stage.title = t("contract:status.notice.scRejected.title");
        stage.message = t("contract:status.notice.scRejected.message");
        stage.buttonText = t("contract:status.notice.scRejected.button");
      } else {
        stage.title = t("contract:status.notice.poRejected.title");
        stage.message = t("contract:status.notice.poRejected.message");
        stage.buttonText = t("contract:status.notice.poRejected.button");
      }
      stage.showButton = true;
      stage.buttonAction = "VIEW_REJECTION_REASON";
      break;

    case "WAIT":
    case "NONE":
    default:
      // 대기 상태
      if (stage.documentType === "SC") {
        stage.title = t("contract:status.notice.scSignaturePending.title");
        stage.message = t("contract:status.notice.scSignaturePending.message");
        stage.buttonText = t(
          "contract:status.notice.scSignaturePending.button",
        );
      } else {
        stage.title = t(
          "contract:status.notice.poSignaturePendingFromClient.title",
        );
        stage.message = t(
          "contract:status.notice.poSignaturePendingFromClient.message",
        );
        stage.buttonText = t(
          "contract:status.notice.poSignaturePendingFromClient.button",
        );
      }
      stage.showButton = false;
      stage.buttonAction = "NONE";
      break;
  }
}
